import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, useAdminApi } from "@/lib/api";
import EnquiryDetail from "./EnquiryDetail";

const TITLES = { player: "Player registrations", franchise: "Franchise enquiries", sponsor: "Sponsor enquiries", media: "Media enquiries", contact: "Contact messages" };
const NAME = { player: "full_name", franchise: "name", sponsor: "brand_name", media: "name", contact: "name" };
const EXTRA = {
  player: [["player_role", "Role"], ["district", "District"], ["state", "State"]],
  franchise: [["company", "Company"], ["territory", "Territory"], ["owner", "Owner"]],
  sponsor: [["sponsorship_interest", "Interest"], ["contact_person", "Contact"], ["owner", "Owner"]],
  media: [["organisation", "Organisation"], ["enquiry_type", "Type"]],
  contact: [["subject", "Subject"]],
};
const selCls = "h-10 w-full rounded-sm border-white/15 bg-[#0A192F] sm:w-48";

export const StatusTag = ({ s }) => {
  const c = s === "approved" || s === "qualified" ? "bg-[#00E5FF]/15 text-[#00E5FF]" : s === "rejected" ? "bg-red-500/15 text-red-300" : s === "new" ? "bg-[#FF5722]/15 text-[#FF8a65]" : "bg-white/10 text-slate-200";
  return <span className={`rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${c}`}>{s.replace(/_/g, " ")}</span>;
};

export default function Enquiries() {
  const { kind } = useParams();
  const [params, setParams] = useSearchParams();
  const req = useAdminApi();
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [pRole, setPRole] = useState("all");
  const openId = params.get("open");

  const load = useCallback(async () => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (status !== "all") qs.set("status", status);
    if (kind === "player" && pRole !== "all") qs.set("player_role", pRole);
    try {
      const d = await req("get", `/admin/enquiries/${kind}?${qs}`);
      setItems(d.items);
      setStatuses(d.statuses);
    } catch (e) { toast.error(errMsg(e)); }
  }, [req, kind, q, status, pRole]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setQ(""); setStatus("all"); setPRole("all"); }, [kind]);

  const exportCsv = async () => {
    try {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (status !== "all") qs.set("status", status);
      const blob = await req("get", `/admin/enquiries/${kind}/export?${qs}`, undefined, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${kind}-enquiries.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const current = items.find((i) => i.id === openId);
  const extras = EXTRA[kind] || [];

  return (
    <div data-testid={`enquiries-${kind}`} className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="font-display text-4xl uppercase">{TITLES[kind]}</h1><p className="text-sm text-slate-400">{items.length} record(s)</p></div>
        <button type="button" onClick={exportCsv} data-testid="export-csv-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors hover:border-[#00E5FF] hover:text-[#00E5FF]">
          <Download className="h-4 w-4" />Export CSV
        </button>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, mobile, city…" data-testid="enquiry-search" className="h-10 rounded-sm border-white/15 bg-[#0A192F] pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className={selCls} data-testid="enquiry-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/15 bg-[#112240] text-white"><SelectItem value="all">All statuses</SelectItem>{statuses.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
        {kind === "player" && (
          <Select value={pRole} onValueChange={setPRole}>
            <SelectTrigger className={selCls} data-testid="enquiry-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/15 bg-[#112240] text-white"><SelectItem value="all">All roles</SelectItem>{["Batter", "Bowler", "All-rounder", "Wicket-keeper"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <div className="overflow-x-auto rounded-md border border-white/10">
        <table data-testid="enquiries-table" className="w-full min-w-[820px] text-left text-sm">
          <thead className="bg-[#112240] text-[11px] uppercase tracking-widest text-slate-400">
            <tr><th className="p-3">Name</th><th className="p-3">Email / Mobile</th>{extras.map(([, l]) => <th key={l} className="p-3">{l}</th>)}<th className="p-3">Status</th><th className="p-3">Received</th></tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.map((i) => (
              <tr key={i.id} data-testid={`enquiry-row-${i.id}`} onClick={() => setParams({ open: i.id })} className="cursor-pointer transition-colors hover:bg-white/5">
                <td className="p-3 font-semibold">{i[NAME[kind]]}</td>
                <td className="p-3 text-slate-300">{i.email}<br /><span className="text-xs text-slate-500">{i.mobile}</span></td>
                {extras.map(([k]) => <td key={k} className="p-3 text-slate-300">{i[k] || "—"}</td>)}
                <td className="p-3"><StatusTag s={i.status} /></td>
                <td className="p-3 text-xs text-slate-400">{new Date(i.created_at).toLocaleDateString("en-IN")}</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={5 + extras.length} className="p-10 text-center text-slate-400">No records found.</td></tr>}
          </tbody>
        </table>
      </div>
      {current && <EnquiryDetail kind={kind} item={current} statuses={statuses} onClose={() => setParams({})} onSaved={(doc) => setItems((all) => all.map((x) => (x.id === doc.id ? doc : x)))} />}
    </div>
  );
}
